'use client';

import { useRef } from 'react';
import type { ReactNode } from 'react';
import { gsap, useMaskedReveal } from '@/components/sections/kit/motion';
import GradientText from '@/components/GradientText';
import GridClearing from '@/components/GridClearing';
import Button from '@/components/sections/kit/Button';

export type FeatureCta = {
  label: string;
  href: string;
  variant?: 'primary' | 'ghost';
};

interface FeatureHeroProps {
  /** Small mono pill above the headline, e.g. "Smart routing". */
  badge?: string;
  /** Headline text before the gradient words. */
  titleBefore: string;
  /** The gradient (accent) words, with the signature glow halo. */
  gradient: string;
  /** Optional headline text after the gradient words. */
  titleAfter?: string;
  /** Sub-headline / lead paragraph. */
  description: ReactNode;
  ctas?: FeatureCta[];
}

/**
 * Shared hero for feature/product pages: an on-grid hero whose text block
 * sits in cleared grid cells (grid-paper look), with the homepage
 * gradient-word + glow treatment. Centered, text-only — each page just
 * supplies copy. Every route mounts <HeroGrid /> so the grid vars resolve.
 */
export default function FeatureHero({
  badge,
  titleBefore,
  gradient,
  titleAfter,
  description,
  ctas = [],
}: FeatureHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);

  // Masked SplitText reveal on the h1; badge/lead/CTAs settle in behind it.
  // The glow halo sits inside the h1, so SplitText's line masks clip it during
  // the reveal — hide it up front and fade it in after the split reverts,
  // instead of letting it pop. The halo also carries `animate-cube-pulse`,
  // whose keyframes animate `opacity` on every frame — a running CSS
  // animation beats inline styles, so plain `opacity` here would be a no-op.
  // `autoAlpha` toggles `visibility` too, which the keyframes never touch, so
  // `visibility: hidden` wins while hidden and the fade-in still shows once
  // visibility flips back to visible (the pulse then resumes on its own).
  // Under reduced motion none of this runs and the halo is simply visible.
  useMaskedReveal(sectionRef, {
    heading: 'h1',
    sub: '[data-hero-sub]',
    prepare: (root) => {
      gsap.set(root.querySelector('[data-hero-halo]'), { autoAlpha: 0 });
    },
    onDone: (root) => {
      gsap.to(root.querySelector('[data-hero-halo]'), {
        autoAlpha: 1,
        duration: 0.8,
        ease: 'power2.out',
      });
    },
  });

  return (
    <section ref={sectionRef} className="relative overflow-hidden pb-16 md:pb-20">
      {/* Whole-cell grid clearing behind the [data-grid-block] text column only. */}
      <GridClearing rootRef={sectionRef} />

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pt-28 md:[padding-top:calc(2*var(--grid-cell,72px))]">
        <div
          data-grid-block
          className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center md:py-8"
        >
          {badge && (
            <div
              data-hero-sub
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600"
              style={{ fontFamily: 'var(--font-jetbrains)' }}
            >
              <span className="h-2 w-2 rounded-full bg-brand-500" />
              {badge}
            </div>
          )}

          <h1 className="text-5xl font-extrabold leading-[1.06] tracking-tight text-slate-900 md:text-6xl">
            {titleBefore}{' '}
            <span className="relative inline-block whitespace-nowrap">
              <span
                data-hero-halo
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[200%] w-[150%] -translate-x-1/2 -translate-y-1/2 animate-cube-pulse rounded-full blur-[70px] [background:radial-gradient(ellipse,rgba(250,67,127,0.4),transparent_70%)]"
              />
              <GradientText colors={['#800938', '#d70e62', '#fa437f']} animationSpeed={2} yoyo={false}>
                {gradient}
              </GradientText>
            </span>
            {titleAfter ? <> {titleAfter}</> : null}
          </h1>

          <p
            data-hero-sub
            className="max-w-xl text-lg leading-relaxed text-slate-600 md:text-xl"
          >
            {description}
          </p>

          {ctas.length > 0 && (
            <div
              data-hero-sub
              className="flex flex-wrap items-center justify-center gap-3"
            >
              {ctas.map((cta) => (
                <Button key={cta.label} href={cta.href} variant={cta.variant}>
                  {cta.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
